import { useState, useEffect, useContext } from "react";
import { ProSidebar, Menu, MenuItem } from "react-pro-sidebar";
import { Box, IconButton, Typography, useMediaQuery, useTheme, Tooltip } from "@mui/material";
import { Link } from "react-router-dom";
import "react-pro-sidebar/dist/css/styles.css";
import { tokens } from "../../theme";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import PeopleOutlinedIcon from "@mui/icons-material/PeopleOutlined";
// import ReceiptOutlinedIcon from "@mui/icons-material/ReceiptOutlined";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import MenuOutlinedIcon from "@mui/icons-material/MenuOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined"; // For Products
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined"; // For Services
import BusinessCenterOutlinedIcon from "@mui/icons-material/BusinessCenterOutlined"; // For Organizations
// import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined"; // For Report
// import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import BusinessIcon from '@mui/icons-material/Business';
// import AssignmentLateIcon from '@mui/icons-material/AssignmentLate';
import HistoryIcon from '@mui/icons-material/History';
// import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
// import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import PeopleIcon from '@mui/icons-material/People';
import CategoryIcon from '@mui/icons-material/Category';
import CancelIcon from '@mui/icons-material/Cancel';
import { AuthContext } from "../../context/AuthContext";
import AssignmentIcon from '@mui/icons-material/Assignment';

const Item = ({ title, to, icon, selected, setSelected }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down('md'));

  const content = (
    <MenuItem
      active={selected === title}
      style={{
        color: colors.grey[100],
      }}
      onClick={() => setSelected(title)}
      icon={icon}
    >
      <Typography>{title}</Typography>
      <Link to={to} />
    </MenuItem>
  );

  return (
    isSmallScreen ? (
      <Tooltip title={title} arrow>
        {content}
      </Tooltip>
    ) : (
      content
    )
  );
};

const Sidebar = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const {currentuser} = useContext(AuthContext);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [selected, setSelected] = useState("Dashboard");
  const [ordersExpanded, setOrdersExpanded] = useState(false);

  const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down('md'));

  useEffect(() => {
    setIsCollapsed(isSmallScreen);
  }, [isSmallScreen]);

  return (
    <Box
      sx={{
        "& .pro-sidebar-inner": {
          background: `${colors.primary[500]} !important`,
        },
        "& .pro-icon-wrapper": {
          backgroundColor: "transparent !important",
        },
        "& .pro-inner-item": {
          padding: "5px 35px 5px 20px !important",
        },
        "& .pro-inner-item:hover": {
          color: "#868dfb !important",
        },
        "& .pro-menu-item.active": {
          color: "#6870fa !important",
        },
      }}
    >
      <ProSidebar collapsed={isCollapsed}>
        <Menu iconShape="square">
          <MenuItem
            onClick={() => setIsCollapsed(!isCollapsed)}
            icon={isCollapsed ? <MenuOutlinedIcon /> : undefined}
            style={{
              margin: "10px 0 10px 0",
              color: colors.grey[100],
            }}
          >
            {!isCollapsed && (
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                ml="15px"
              >
                <Typography variant="h5" color={colors.grey[100]}>
                  EthioGlobal
                </Typography>
                <IconButton onClick={() => setIsCollapsed(!isCollapsed)}>
                  <MenuOutlinedIcon />
                </IconButton>
              </Box>
            )}
          </MenuItem>

          <Box paddingLeft={isCollapsed ? undefined : "8%"}>
            <Item
              title="Ride"
              to="/ride"
              icon={<DirectionsCarIcon />}
              selected={selected}
              setSelected={setSelected}
            />
            <Item
              title="Drivers"
              to="/drivers"
              icon={<PersonOutlinedIcon />}
              selected={selected}
              setSelected={setSelected}
            />
            <Item
              title="Vehicles"
              to="/vehicles"
              icon={<DirectionsCarIcon />}
              selected={selected}
              setSelected={setSelected}
            />
            <Item
              title="Users"
              to="/users"
              icon={<PeopleOutlinedIcon />}
              selected={selected}
              setSelected={setSelected}
            />
            <Item
              title="Suppliers"
              to="/suppliers" // Update route if needed
              icon={<BusinessIcon />}
              selected={selected}
              setSelected={setSelected}
            />
            <MenuItem
              onClick={() => setOrdersExpanded(!ordersExpanded)}
              icon={<AssignmentIcon />}
              selected={selected}
              setSelected={setSelected}
            >
              Orders
            </MenuItem>
            {ordersExpanded && (
              <Box pl={4}> {/* Indent the sub-menu items */}
                <Item
                  title="New Requests"
                  to="/requests"
                  icon={<RequestQuoteIcon />}
                  selected={selected}
                  setSelected={setSelected}
                />
                <Item
                  title="Active Orders"
                  to="/active-orders"
                  icon={<PlayArrowIcon />}
                  selected={selected}
                  setSelected={setSelected}
                />
                <Item
                  title="Cancelled Orders"
                  to="/cancelled-orders"
                  icon={<CancelIcon />}
                  selected={selected}
                  setSelected={setSelected}
                />
                <Item
                  title="Pending Orders"
                  to="/pending-orders"
                  icon={<HourglassEmptyIcon />}
                  selected={selected}
                  setSelected={setSelected}
                />
                <Item
                  title="Order History"
                  to="/history"
                  icon={<HistoryIcon />}
                  selected={selected}
                  setSelected={setSelected}
                />
              </Box>
            )}
            <Item
              title="Customers"
              to="/customers" // Update route if needed
              icon={<PeopleIcon />}
              selected={selected}
              setSelected={setSelected}
            />
            <Item
              title="Categories"
              to="/categories" // Update route if needed
              icon={<CategoryIcon />}
              selected={selected}
              setSelected={setSelected}
            />
            <Item
              title="Products"
              to="/products" // Update route if needed
              icon={<Inventory2OutlinedIcon />}
              selected={selected}
              setSelected={setSelected}
            />
            <Item
              title="Services"
              to="/services" // Update route if needed
              icon={<LocalOfferOutlinedIcon />}
              selected={selected}
              setSelected={setSelected}
            />
            <Item
              title="Organizations"
              to="/organizations" // Update route if needed
              icon={<BusinessCenterOutlinedIcon />}
              selected={selected}
              setSelected={setSelected}
            />
          </Box>
        </Menu>
      </ProSidebar>
    </Box>
  );
};

export default Sidebar;